/**
 * iOS 大标题：内容滚动时，顶部渐显玻璃栏 + 收紧小标题，大标题同步缩小淡出。
 *
 * 为什么不用 CSS 滚动驱动动画：`animation-timeline: scroll()` 目前 Safari 不支持，
 * 这是手机优先的项目，Safari 必须能用。
 * 为什么不用 `window.addEventListener('scroll')`：逐帧回调 + React 状态更新会掉帧，
 * 这条是明令禁止的。ScrollTrigger 内部用统一的 rAF 调度，同一个滚动量只算一次。
 */

import { useGSAP } from '@gsap/react';
import { useRef, type RefObject } from 'react';

import { MOTION, gsap, prefersReducedMotion } from './gsap';

export interface LargeTitleOptions {
	/** 内部滚动容器 */
	scrollerRef: RefObject<HTMLElement>;
	/** 外层容器，用于限定查询范围 */
	shellRef: RefObject<HTMLElement>;
	/** 变化时重建（路由切换会换掉大标题元素） */
	deps: unknown[];
}

/**
 * 滚过约 44px 后玻璃栏与紧缩标题出现，大标题同时缩小淡出。
 * 全程只改 transform 和 opacity。
 */
export function useLargeTitle({ scrollerRef, shellRef, deps }: LargeTitleOptions): void {
	useGSAP(
		() => {
			const scroller = scrollerRef.current;
			const shell = shellRef.current;
			if (!scroller || !shell) return;

			const large = scroller.querySelector<HTMLElement>('[data-large-title]');
			const glass = shell.querySelector<HTMLElement>('[data-topbar-glass]');
			const compact = shell.querySelector<HTMLElement>('[data-topbar-title]');
			if (!large || !glass || !compact) return;

			if (prefersReducedMotion()) {
				// 减弱动态效果时不做滚动联动，但顶部栏要直接给上背景，
				// 否则滚下去之后内容会压在紧缩标题下面
				gsap.set(glass, { autoAlpha: 1 });
				gsap.set(compact, { autoAlpha: 0 });
				return;
			}

			const range = {
				trigger: large,
				scroller,
				start: 'top top+=48',
				end: 'top top+=4',
			};

			gsap.fromTo(
				glass,
				{ autoAlpha: 0 },
				{ autoAlpha: 1, ease: 'none', scrollTrigger: { ...range, scrub: true } },
			);

			gsap.fromTo(
				compact,
				{ autoAlpha: 0, y: 8 },
				{ autoAlpha: 1, y: 0, ease: MOTION.ease.out, scrollTrigger: { ...range, scrub: 0.4 } },
			);

			// 大标题自己也要有反应，否则它只是"滚走了"，不像系统行为
			gsap.fromTo(
				large,
				{ scale: 1, autoAlpha: 1 },
				{
					scale: 0.92,
					autoAlpha: 0,
					transformOrigin: 'left top',
					ease: 'none',
					scrollTrigger: { ...range, scrub: true },
				},
			);

			return () => {
				for (const element of [glass, compact, large]) {
					gsap.killTweensOf(element);
					gsap.set(element, { clearProps: 'all' });
				}
			};
		},
		{ scope: shellRef, dependencies: deps, revertOnUpdate: true },
	);
}

/**
 * 路由切换时的入场。
 * iOS 切标签几乎没有出场动画：内容直接换，新内容轻微上浮淡入。
 * 做出场反而会显得拖沓，所以只做入场。实现见 useEnter.ts。
 */
export { useEnterIn as useRouteEnter } from './useEnter';
